import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Generic types for API state management
export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  cacheTimeout: number;
}

export interface ApiArrayState<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  cacheTimeout: number;
}

export interface PaginatedState<T> {
  data: T[];
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  filters: Record<string, any>;
}

// Cache utilities
export const CACHE_DURATION = {
  SHORT: 5 * 60 * 1000,    // 5 minutes
  MEDIUM: 15 * 60 * 1000,  // 15 minutes
  LONG: 60 * 60 * 1000,    // 1 hour
};

export const isCacheValid = (lastFetch: number | null, cacheTimeout: number): boolean => {
  if (!lastFetch) return false;
  return Date.now() - lastFetch < cacheTimeout;
};

// Generic reducers for API state
export const createApiSliceState = <T>(cacheTimeout = CACHE_DURATION.MEDIUM): ApiState<T> => ({
  data: null,
  loading: false,
  error: null,
  lastFetch: null,
  cacheTimeout,
});

export const createApiArraySliceState = <T>(cacheTimeout = CACHE_DURATION.MEDIUM): ApiArrayState<T> => ({
  data: [],
  loading: false,
  error: null,
  lastFetch: null,
  cacheTimeout,
});

export const createPaginatedSliceState = <T>(pageSize = 10): PaginatedState<T> => ({
  data: [],
  totalRecords: 0,
  currentPage: 1,
  pageSize,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
});

// Generic reducer handlers
export const createApiReducers = <T>() => ({
  setPending: (state: ApiState<T>) => {
    state.loading = true;
    state.error = null;
  },
  setFulfilled: (state: ApiState<T>, action: PayloadAction<T>) => {
    state.loading = false;
    state.data = action.payload;
    state.error = null;
    state.lastFetch = Date.now();
  },
  setRejected: (state: ApiState<T>, action: PayloadAction<string>) => {
    state.loading = false;
    state.error = action.payload;
  },
  clearError: (state: ApiState<T>) => {
    state.error = null;
  },
  clearData: (state: ApiState<T>) => {
    state.data = null;
    state.lastFetch = null;
    state.error = null;
  },
  invalidateCache: (state: ApiState<T>) => {
    state.lastFetch = null;
  },
});

export const createApiArrayReducers = <T>() => ({
  setPending: (state: ApiArrayState<T>) => {
    state.loading = true;
    state.error = null;
  },
  setFulfilled: (state: ApiArrayState<T>, action: PayloadAction<T[]>) => {
    state.loading = false;
    state.data = action.payload;
    state.error = null;
    state.lastFetch = Date.now();
  },
  setRejected: (state: ApiArrayState<T>, action: PayloadAction<string>) => {
    state.loading = false;
    state.error = action.payload;
  },
  clearError: (state: ApiArrayState<T>) => {
    state.error = null;
  },
  clearData: (state: ApiArrayState<T>) => {
    state.data = [];
    state.lastFetch = null;
    state.error = null;
  },
  invalidateCache: (state: ApiArrayState<T>) => {
    state.lastFetch = null;
  },
  addItem: (state: ApiArrayState<T>, action: PayloadAction<T>) => {
    state.data.push(action.payload);
  },
  updateItem: (state: ApiArrayState<T>, action: PayloadAction<{ id: number; data: Partial<T> }>) => {
    const index = state.data.findIndex((item: any) => item.id === action.payload.id);
    if (index !== -1) {
      state.data[index] = { ...state.data[index], ...action.payload.data } as T;
    }
  },
  removeItem: (state: ApiArrayState<T>, action: PayloadAction<number>) => {
    state.data = state.data.filter((item: any) => item.id !== action.payload);
  },
});

// Utility function to create async thunks with caching
export const createCachedAsyncThunk = <T>(
  typePrefix: string,
  apiCall: () => Promise<T>,
  selectState: (state: any) => ApiState<T> | ApiArrayState<T>
) => {
  return createAsyncThunk<T, void, { rejectValue: string }>(
    typePrefix,
    async (_, { getState, rejectWithValue }) => {
      try {
        const state = selectState(getState());
        
        // Check cache validity
        if (state.data && isCacheValid(state.lastFetch, state.cacheTimeout)) {
          return state.data as T;
        }
        
        return await apiCall();
      } catch (error: any) {
        return rejectWithValue(error.message || 'An error occurred');
      }
    }
  );
};
